import type { LucideIcon } from "lucide-react";
import { cn } from "@strivelabs/ui-core/lib/utils";
import { ArtifactIcon } from "@/components/common/ArtifactIcon";

export interface ArtifactCardProps {
  title: string;
  description?: string | null;
  /** The mark in the leading slot; the artifact's own tile unless the card is for something else. */
  icon?: LucideIcon;
  /** Shown as a badge once an artifact has been rewritten at least once. */
  version?: number | null;
  /** True while this artifact's canvas is the open one. */
  isOpen?: boolean;
  /** Omitted where there is no canvas to open, leaving a plain receipt. */
  onOpen?: () => void;
}

const CARD_CLASS =
  "flex w-full items-center gap-[11px] rounded-[10px] border bg-white px-3.5 py-[11px] text-left transition-[border-color,box-shadow]";

/**
 * The card's contents, identical whether or not it can be opened.
 */
function ArtifactCardContents({ title, description, version, icon: Icon }: ArtifactCardProps) {
  return (
    <>
      {Icon ? (
        // Not an artifact, so not the artifact's cream tile — but the same
        // footprint, so a list mixing the two keeps its text on one line.
        <span aria-hidden className="inline-flex size-7 shrink-0 items-center justify-center">
          <Icon className="size-[18px] text-[#9A9A9A]" />
        </span>
      ) : (
        <ArtifactIcon />
      )}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium tracking-[-0.01em] text-[#181818]">
          {title}
        </span>
        {description ? (
          <span className="truncate text-[12.5px] tracking-[-0.01em] text-[#9A9A9A]">
            {description}
          </span>
        ) : null}
      </span>
      {version && version > 1 ? (
        <span className="shrink-0 text-[12.5px] font-medium tracking-[-0.01em] text-[#9A9A9A]">
          v{version}
        </span>
      ) : null}
    </>
  );
}

/**
 * An artifact in the transcript: the receipt for a page the agent just made,
 * and the way back into it. Kept separate from the block renderer so the same
 * card can list a task's notes and outputs when those arrive.
 */
export function ArtifactCard(props: ArtifactCardProps) {
  const { title, isOpen, onOpen } = props;

  if (!onOpen) {
    return (
      <div className={cn(CARD_CLASS, "border-[rgba(0,0,0,0.08)]")}>
        <ArtifactCardContents {...props} />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={`Open ${title}`}
      aria-current={isOpen ? "true" : undefined}
      className={cn(
        CARD_CLASS,
        isOpen
          ? "border-[#9DB9F0] bg-[#FBFAF7] shadow-[0_0_0_1px_#9DB9F0]"
          : "border-[rgba(0,0,0,0.08)] hover:border-[rgba(0,0,0,0.14)] hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]",
      )}
    >
      <ArtifactCardContents {...props} />
    </button>
  );
}
